/*
 *
 * PaymentMethodsV2 reducer
 *
 */
import produce from 'immer';
import {
  DEFAULT_ACTION,
  UPDATE_FILTERS,
  RESET_FILTERS,
  ENABLE_LOADER,
  DISABLE_LOADER,
  HANDLE_PAYMENT_METHODS_RESPONSE,
  SAVE_PAYTM_DATA,
  SAVE_PAYTM_PROD_CONFIG,
  SAVE_PAYTM_TEST_CONFIG,
  HANDLE_PAYPAL_CONFIG_RESPONSE,
  HANDLE_UPDATE_DEFAULT_PAYPAL_CURRENCY,
  HANDLE_DEACTIVATE_PAYPAL_CURRENCY,
  HANDLE_ACTIVATE_PAYPAL_CURRENCY,
  HANDLE_ENABLE_PAYMENT_METHODS_RESPONSE,
  CASH_ON_DELIVERY,
  CASH,
} from './constants';

const getDefaultFilterState = () => ({
  filters: { appliedFilters: {} },
  filtersForDisplay: [],
});

export const initialState = {
  isLoading: false,
  orgType: '',
  mcc: '',
  paymentCodeMapping: {},
  paymentMethodsData: {},
  paytmProdConfig: {},
  paytmTestConfig: {},
  paypalAccount: {},
  defaultPaypalCurrency: '',
  activePaypalCurrencies: [],
  paypalCurrencies: [],
  ...getDefaultFilterState(),
};

/* eslint-disable default-case, no-param-reassign */
const paymentMethodsV2Reducer = (state = initialState, action) =>
  produce(state, draft => {
    switch (action.type) {
      case ENABLE_LOADER: {
        draft.isLoading = true;
        break;
      }
      case DISABLE_LOADER: {
        draft.isLoading = false;
        break;
      }
      case HANDLE_PAYMENT_METHODS_RESPONSE: {
        draft.paymentMethodsData = {};

        if (action.data) {
          const { OrgType, mcc, paymentModes } = action.data;

          // format the data
          const data = paymentModes?.reduce((acc, obj) => {
            if (
              obj.uniqueIdentifier === CASH_ON_DELIVERY ||
              obj.uniqueIdentifier === CASH
            ) {
              acc[CASH_ON_DELIVERY] = obj;
            } else {
              acc[obj.uniqueIdentifier] = obj;
            }
            return acc;
          }, {});
          //
          const paymentCodeMapping = {};
          // create a mapping to be used later for lookup and updating the status
          paymentModes.forEach(({ attributes, uniqueIdentifier }) => {
            attributes.forEach(attribute => {
              attribute.attributes.forEach(obj => {
                obj.paymentCode.forEach(code => {
                  paymentCodeMapping[code] = {
                    key: uniqueIdentifier,
                    subKey: attribute.uniqueIdentifier,
                    parentCode: obj.parentCode,
                  };
                });
              });
            });
          });

          draft.paymentCodeMapping = paymentCodeMapping;
          draft.paymentMethodsData = data;
          draft.orgType = OrgType;
          draft.mcc = mcc;
        } else {
          draft.orgType = '';
          draft.mcc = '';
        }
        break;
      }
      case UPDATE_FILTERS: {
        const { appliedFilters = {}, ...filters } = action.data;

        if (appliedFilters) draft.filters.appliedFilters = appliedFilters;

        draft.filters = {
          ...draft.filters,
          ...filters,
        };

        break;
      }
      case RESET_FILTERS: {
        const { filters, filtersForDisplay } = getDefaultFilterState();
        draft.filters = filters;
        draft.filtersForDisplay = filtersForDisplay;
        break;
      }
      case SAVE_PAYTM_DATA: {
        const { paytmProdConfig, paytmTestConfig } = action.data;
        draft.paytmProdConfig = paytmProdConfig;
        draft.paytmTestConfig = paytmTestConfig;
        break;
      }
      case SAVE_PAYTM_PROD_CONFIG: {
        draft.paytmProdConfig = action.data;
        break;
      }
      case SAVE_PAYTM_TEST_CONFIG: {
        draft.paytmTestConfig = action.data;
        break;
      }
      case HANDLE_PAYPAL_CONFIG_RESPONSE: {
        const {
          activeAccount,
          defaultCurrency,
          activeCurrencies,
          paypalCurrencies,
        } = action.data;

        if (activeAccount) {
          draft.paypalAccount = activeAccount;

          if (defaultCurrency && defaultCurrency.currencyCode)
            draft.defaultPaypalCurrency = defaultCurrency.currencyCode;

          if (activeCurrencies && activeCurrencies.length > 0)
            draft.activePaypalCurrencies = activeCurrencies
              .map(({ isActive, currencyCode }) =>
                isActive ? currencyCode : '',
              )
              .filter(obj => !!obj);

          if (paypalCurrencies && paypalCurrencies.length > 0)
            draft.paypalCurrencies = paypalCurrencies
              .map(({ currencyCode, currency }) => ({
                key: currencyCode,
                value: currencyCode,
                text: currency,
              }))
              .filter(obj => !!obj);
        } else {
          draft.paypalAccount = {};
        }

        break;
      }
      case HANDLE_UPDATE_DEFAULT_PAYPAL_CURRENCY: {
        draft.defaultPaypalCurrency = action.data.currencyCode;
        break;
      }
      case HANDLE_DEACTIVATE_PAYPAL_CURRENCY: {
        const { currencyCode } = action.data;
        draft.activePaypalCurrencies = draft.activePaypalCurrencies.filter(
          elem => elem !== currencyCode,
        );
        draft.isLoading = false;
        break;
      }
      case HANDLE_ACTIVATE_PAYPAL_CURRENCY: {
        const { currencyCode } = action.data;
        if (!draft.activePaypalCurrencies.includes(currencyCode))
          draft.activePaypalCurrencies.push(currencyCode);
        break;
      }
      case HANDLE_ENABLE_PAYMENT_METHODS_RESPONSE: {
        // logic to find and update the existing data in order to avoid the time taking get call for payment methods
        const paymentCodeStatusMapping = {};
        action.data.forEach(obj => {
          const { parentCode, ...rest } = draft.paymentCodeMapping[
            obj.paymentModeCode
          ];

          if (parentCode) {
            paymentCodeStatusMapping[parentCode] = {
              status: obj.status,
              ...rest,
            };
          }
        });

        Object.keys(paymentCodeStatusMapping).forEach(parentCode => {
          const { status, key, subKey } = paymentCodeStatusMapping[parentCode];
          const index = draft.paymentMethodsData[key]?.attributes?.findIndex(
            obj => obj.uniqueIdentifier === subKey,
          );
          const result = JSON.parse(JSON.stringify(draft.paymentMethodsData));

          if (index >= 0) {
            const { attributes } = draft.paymentMethodsData[key].attributes[
              index
            ];
            const subIndex = attributes.findIndex(
              obj => String(obj.parentCode) === String(parentCode),
            );

            if (subIndex >= 0) {
              result[key].attributes[index].attributes[
                subIndex
              ].status = status;
              draft.paymentMethodsData = result;
            }
          }
        });
        break;
      }
      case DEFAULT_ACTION:
        break;
    }
  });

export default paymentMethodsV2Reducer;
